import moment from 'moment'
import { createSelector } from 'reselect'
import { RootState } from '../../../store/rootState'
import { IPagination } from '../../../types/pagination'
import { sortTasks, sortTasksByKey } from "../../../utils/sort"
import { statusColor } from '../../../utils/statusColor'
import { milestoneSharePercent } from '../reducers/accountingReducer'
import { IDirectCostInvoice } from '../types/invoice'
import { IProject } from '../types/project'
import { TaskStatus } from '../types/projectActivity'
import {
  ICostFilter,
  ICostsClustering,
  ICostSorting,
  ICostWidgetTotal,
  IDirectCost,
  IMilestone,
  IMilestoneOfferDirectCostMonths,
  IMilestoneSortColumn,
  IMilestoneWithUnitTracker,
  IOfferDirectCost,
  IOfferPassThroughCost,
  IPassThroughCost
} from './../types/directAndPassTroughCosts'

export const remainingTime = (current_hours: number, total_hours: number): string[] => {
  const _remaining = `${(total_hours - current_hours)}`
  const _remainingPercentage = total_hours !== 0 || current_hours !== 0 ? `${(100 - (current_hours / total_hours) * 100).toFixed(0)}%` : '0%'
  if (_remainingPercentage === 'NaN%' || _remainingPercentage === '-Infinity%') {
    return [_remaining, '-']
  }
  return [_remaining, _remainingPercentage]
}

export const calcProfit = (budget: number, forecast: number): number => {
  const profit = budget !== 0 ? (1 - (forecast / budget)) * 100 : 0
  return profit
}

const calcHealth = (dc: IDirectCost) => {
  let percentage = 0
  if (dc.budget && +dc.budget !== 0 && dc.forecast && +dc.forecast !== 0) {
    percentage = dc.forecast / dc.budget
  }
  const health = percentage > 1.05 ? 'off_track' : percentage > .95 ? 'at_risk' : 'on_track'
  return health
}

const getFilterCostsCount = (state: RootState) => state.projectsState.costFilters
const getSortClustering = (state: RootState) => state.projectsState.sortCosts
const getSearchValue = (state: RootState) => state.projectsState.searchCosts
const getPtcSort = (state: RootState) => state.projectsState.sortPtc
export const getDirectCostsState = (state: RootState): IDirectCost[] => state.projectsState.directCosts
export const getPaginatedDirectCostsState = (state: RootState): IPagination<IDirectCost[]> => state.projectsState.paginatedDirectCosts
export const getPassTroughCostsState = (state: RootState): IPassThroughCost[] => state.projectsState.passTroughCosts
export const getOfferDirectCostState = (state: RootState): IOfferDirectCost[] => state.projectsState.offerDirectCosts
export const getOfferPassTroughCostsState = (state: RootState): IOfferPassThroughCost[] => state.projectsState.offerPassTroughCosts
const getDCSort = (state: RootState) => state.projectsState.sortDirectCostInvoices
const getDirectCostInvoices = (state: RootState): IDirectCostInvoice[] => state.projectsState.directCostInvoices
const getProjectForShares = (state: RootState): IProject => state.projectsState.project
const getMilestones = (state: RootState): IMilestone[] => state.projectsState.milestones
const getSortTasks = (state: RootState): IMilestoneSortColumn => state.projectsState.sortMilestoneClusteredTasks
const getMilestoneUnitTracker = (state: RootState): IMilestoneOfferDirectCostMonths[] => state.projectsState.milestoneUnitTracker

const sortedDirectCosts = (invoices: IDirectCostInvoice[], sort: { name: string, direction: string }): IDirectCostInvoice[] => {
  if (sort.name === 'number') {
    return invoices.toSorted((a, b) => {
      if (sort.direction === 'asc') {
        return a.number.localeCompare(b.number)
      } else {
        return b.number.localeCompare(a.number)
      }
    })
  } else if (sort.name === 'description') {
    return invoices.toSorted((a, b) => {
      if (sort.direction === 'asc') {
        return a.description.localeCompare(b.description)
      } else {
        return b.description.localeCompare(a.description)
      }
    })
  } else if (sort.name === 'presumed_date') {
    return invoices.toSorted((a, b) => {
      if (sort.direction === 'asc') {
        return moment(a.presumed_date, 'YYYY-MM-DD').toDate().getTime() - moment(b.presumed_date, 'YYYY-MM-DD').toDate().getTime()
      } else {
        return moment(b.presumed_date, 'YYYY-MM-DD').toDate().getTime() - moment(a.presumed_date, 'YYYY-MM-DD').toDate().getTime()
      }
    })
  } else if (sort.name === 'invoiced_amount') {
    return invoices.toSorted((a, b) => {
      if (sort.direction === 'asc') {
        return +a.invoiced_amount - +b.invoiced_amount
      } else {
        return +b.invoiced_amount - +a.invoiced_amount
      }
    })
  } else if (sort.name === 'percent_of_total') {
    return invoices.toSorted((a, b) => {
      if (sort.direction === 'asc') {
        return +a.percent_of_total - +b.percent_of_total
      } else {
        return +b.percent_of_total - +a.percent_of_total
      }
    })
  } else if (sort.name === 'invoiced_on') {
    return invoices.toSorted((a, b) => {
      const aParsed = a.paid_on ? moment(a.invoiced_on, 'YYYY-MM-DD').toDate().getTime() : 0
      const bParsed = b.paid_on ? moment(b.invoiced_on, 'YYYY-MM-DD').toDate().getTime() : 0
      if (sort.direction === 'asc') {
        return aParsed - bParsed
      } else {
        return bParsed - aParsed
      }
    })
  } else if (sort.name === 'paid_on') {
    return invoices.toSorted((a, b) => {
      const aParsed = a.paid_on ? moment(a.paid_on, 'YYYY-MM-DD').toDate().getTime() : 0
      const bParsed = b.paid_on ? moment(b.paid_on, 'YYYY-MM-DD').toDate().getTime() : 0
      if (sort.direction === 'asc') {
        return aParsed - bParsed
      } else {
        return bParsed - aParsed
      }
    })
  } else if (sort.name === 'status') {
    return invoices.toSorted((a, b) => {
      const aParsedPaidOn = a.paid_on ? moment(a.paid_on, 'YYYY-MM-DD').toDate().getTime() : 0
      const bParsedPaidOn = b.paid_on ? moment(b.paid_on, 'YYYY-MM-DD').toDate().getTime() : 0
      const aParsedInvoicedOn = a.invoiced_on ? moment(a.invoiced_on, 'YYYY-MM-DD').toDate().getTime() : 0
      const bParsedInvoicedOn = b.invoiced_on ? moment(b.invoiced_on, 'YYYY-MM-DD').toDate().getTime() : 0
      if (sort.direction === 'asc') {
        return aParsedInvoicedOn !== bParsedInvoicedOn ?
          aParsedInvoicedOn - bParsedInvoicedOn :
          aParsedPaidOn - bParsedPaidOn
      } else {
        return bParsedInvoicedOn !== aParsedInvoicedOn ?
          bParsedInvoicedOn - aParsedInvoicedOn :
          bParsedPaidOn - aParsedPaidOn
      }
    })
  }
  return invoices
}

/**
 * The project's direct-cost invoices, searched, share-annotated and sorted.
 *
 * percent_of_total is computed here rather than in the reducer for two reasons: the
 * denominator is the project's rebated study total, which a slice reducer cannot reach,
 * and the "% of Total" column is sortable — sortedDirectCosts reads percent_of_total, so
 * the annotation has to land before the sort (issue #120).
 */
export const getDirectCostInvoicesSelector = createSelector(
  [getDirectCostInvoices, getSearchValue, getDCSort, getProjectForShares],
  (directCostInvoices, searchCosts, getDCSort, project) => {
    const withShares = (invoices: IDirectCostInvoice[]) =>
      invoices.map(inv => ({ ...inv, percent_of_total: milestoneSharePercent(inv, project) }))
    if (searchCosts.search !== '') {
      return sortedDirectCosts(withShares(directCostInvoices.filter(
        a => a.number.toLowerCase().includes(searchCosts.search.toLowerCase())
      )), getDCSort)
    }
    return sortedDirectCosts(withShares(directCostInvoices), getDCSort)
  }
)

export const getDirectCostInvCashFlow = createSelector(
  [getDirectCostInvoices],
  (directCostInvoices) => {
    return directCostInvoices.filter(a => a.paid_on)
  }
)

// PASS TROUGH COSTS
export const getPassTroughCostsSelector = createSelector(
  [getPassTroughCostsState],
  (ptc: IPassThroughCost[]) => {
    return ptc.map(cost => {
      const budget = +cost.unit_price * +cost.quantity
      return {
        ...cost,
        current_hours: +cost.current_hours,
        total_hours: +cost.total_hours,
        statusColor: (status: string) => statusColor(status),
        time_completion: +cost.time_completion,
        remainingHours: remainingTime(+cost.current_hours, +cost.total_hours)[0],
        remainingHoursPercentage: remainingTime(+cost.current_hours, +cost.total_hours)[1],
        expenditures: cost.expenditures ? parseFloat(cost.expenditures.toString()) : 0,
        project_phases: cost.project_phases,
        total_cost: +cost.unit_price * +cost.quantity,
        budget: budget
      }
    })
  }
)

const sortPassTroughCosts = (costs: IPassThroughCost[], sortClustering: ICostSorting) => {
  if (sortClustering) {
    let sortedCosts = [...costs]
    if (sortClustering.column.name === 'supplier') {
      sortedCosts.toSorted((a, b) => a.supplier.localeCompare(b.supplier))
    } else if (sortClustering.column.name === 'offer_position') {
      sortedCosts = sortTasksByKey(sortedCosts, 'key', 'offer_position')
    } else if (sortClustering.column.name === 'timelines') {
      sortedCosts.toSorted((a, b) => moment(a.end_date).diff(moment(b.end_date)))
    } else if (sortClustering.column.name === 'status') {
      sortedCosts.toSorted((a, b) => a.position_status.localeCompare(b.position_status))
    } else if (sortClustering.column.name === 'time progress') {
      sortedCosts.toSorted((a, b) => +a.time_completion - +b.time_completion)
    } else if (sortClustering.column.name === 'tracked hours') {
      sortedCosts.toSorted((a, b) => +a.current_hours !== +b.current_hours ? +a.current_hours - +b.current_hours : +a.total_hours - +b.total_hours)
    } else if (sortClustering.column.name === 'remaining') {
      sortedCosts.toSorted((a, b) => +a.remainingHours - +b.remainingHours)
    } else if (sortClustering.column.name === 'quantity') {
      sortedCosts.toSorted((a, b) => +a.quantity - +b.quantity)
    } else if (sortClustering.column.name === 'unit price') {
      sortedCosts.toSorted((a, b) => +a.unit_price - +b.unit_price)
    } else if (sortClustering.column.name === 'total costs') {
      sortedCosts.toSorted((a, b) => (+a.unit_price * +a.quantity) - (+b.unit_price * +b.quantity))
    } else if (sortClustering.column.name === 'total hours') {
      sortedCosts.toSorted((a, b) => a.total_hours && b.total_hours ? +a.total_hours - +b.total_hours : 1)
    }

    if (sortClustering.column.direction === 'desc') {
      sortedCosts.reverse()
    }
    return sortedCosts
  }
  return costs
}

const sortPtc = (costs: IPassThroughCost[], sort: { name: string, direction: string }) => {
  let sortedCosts = [...costs]
  if (sort.name === 'supplier') {
    sortedCosts = sortTasks(sortedCosts, 'supplier')
  } else if (sort.name === 'position') {
    sortedCosts = sortTasksByKey(sortedCosts, 'key', 'offer_position')
  } else if (sort.name === 'total_cost') {
    sortedCosts.toSorted((a, b) => a.total_cost && b.total_cost ? a.total_cost - b.total_cost : -1)
  } else if (sort.name === 'thereof_paid') {
    sortedCosts.toSorted((a, b) => +a.thereof_paid - +b.thereof_paid)
  } else if (sort.name === 'passed_through_amount') {
    sortedCosts.toSorted((a, b) => a.passed_through_amount - b.passed_through_amount)
  }
  if (sort.direction === 'desc') {
    sortedCosts.reverse()
  }
  return sortedCosts
}

export const getPTCostSearchSelector = createSelector(
  [getPassTroughCostsSelector, getSearchValue, getPtcSort],
  (pass_trough_costs, searchValue, sort) => {
    if (searchValue.search !== '') {
      return sortPtc(pass_trough_costs.filter(
        ptc => ptc.offer_position.toLowerCase().includes(searchValue.search.toLowerCase())
      ), sort)
    }
    return sortPtc(pass_trough_costs, sort)
  }
)

const passTroughCostClustering = (_costs: IPassThroughCost[]): ICostsClustering => {
  const clustered: ICostsClustering = {
    name: 'Pass-Through-Costs',
    costs: _costs
  }

  return clustered
}

export const getClusteredPassTroughCostsSelector = createSelector(
  [getSortClustering, getPTCostSearchSelector],
  (sortClustering: ICostSorting, costs: IPassThroughCost[]) => {
    if (costs) {
      const sortedCosts = sortPassTroughCosts(costs, sortClustering)
      return passTroughCostClustering(sortedCosts)
    }
  }
)

// DIRECT COSTS
export const getDirectCostsSelector = createSelector(
  [getDirectCostsState],
  (directCosts: IDirectCost[]) => {
    return sortTasksByKey(directCosts, 'key', 'offer_position').map(cost => {
      const budget = cost.budget ? cost.budget : +cost.unit_price * +cost.quantity
      return {
        ...cost,
        current_hours: +cost.current_hours,
        total_hours: +cost.total_hours,
        forecast: cost.forecast ? +cost.forecast : 0,
        forecast_hours: cost.forecast_hours ? +cost.forecast_hours : 0,
        statusColor: (status: string) => statusColor(status),
        time_completion: +cost.time_completion,
        remainingHours: remainingTime(+cost.current_hours, cost.new_total_hours ? cost.new_total_hours : +cost.total_hours)[0],
        remainingHoursPercentage: remainingTime(+cost.current_hours, cost.new_total_hours ? cost.new_total_hours : +cost.total_hours)[1],
        expenditures: cost.expenditures ? parseFloat(cost.expenditures.toString()) : 0,
        department: cost.department,
        project_phase: cost.project_phase,
        profit: +calcProfit(cost.budget ? cost.budget : budget, cost.forecast ? cost.forecast : 0).toFixed(2),
        budget: budget
      }
    })
  }
)

export const getDirectCostsInDanger = createSelector(
  [getDirectCostsState],
  (directCosts) => {
    const dcs = directCosts.map(dc => {
      return {
        ...dc,
        health: calcHealth(dc),
        current_hours: +dc.current_hours,
        total_hours: +dc.total_hours,
        forecast_hours: dc.forecast_hours ? +dc.forecast_hours : 0,
        estimate_overdue: dc.estimate_overdue ? +dc.estimate_overdue : 0,
        statusColor: (status: string) => statusColor(status),
        remainingHours: remainingTime(+dc.current_hours, dc.new_total_hours ? dc.new_total_hours : +dc.total_hours)[0],
        remainingHoursPercentage: remainingTime(+dc.current_hours, dc.new_total_hours ? dc.new_total_hours : +dc.total_hours)[1]
      }
    })
    return dcs.filter(dc =>
      dc.status === 'in_progress'
      && (dc.estimate_overdue
        && dc.current_hours
        && dc.estimate_overdue + dc.current_hours > (dc.new_total_hours ? dc.new_total_hours : +dc.total_hours)
        || dc.forecast_hours > (dc.new_total_hours ? dc.new_total_hours : +dc.total_hours)))
  }
)

export const getPaginatedDirectCostsInDanger = createSelector(
  [getPaginatedDirectCostsState],
  (directCosts) => {
    const dcs = directCosts.results.map(dc => {
      return {
        ...dc,
        health: calcHealth(dc),
        current_hours: +dc.current_hours,
        total_hours: +dc.total_hours,
        forecast_hours: dc.forecast_hours ? +dc.forecast_hours : 0,
        estimate_overdue: dc.estimate_overdue ? +dc.estimate_overdue : 0,
        statusColor: (status: string) => statusColor(status),
        remainingHours: remainingTime(+dc.current_hours, dc.new_total_hours ? dc.new_total_hours : +dc.total_hours)[0],
        remainingHoursPercentage: remainingTime(+dc.current_hours, dc.new_total_hours ? dc.new_total_hours : +dc.total_hours)[1]
      }
    })
    return {
      ...directCosts,
      results: dcs
    }
  }
)

export const getFilterCountSelector = createSelector(
  getFilterCostsCount,
  filters => {
    const count = filters.map(f => f.options)
    const _filters = count.flat().filter(o => o.selected)
    return _filters.length
  }
)

const flattenFilters = (type: string, filters: ICostFilter[]) => {
  return filters
    .filter(f => f.name === type)
    .map(f => f.options.map((opt) => opt))
    .flat()
    .filter(o => o.selected)
    .map(o => o.value)
}

const statusClustering = (_costs: IDirectCost[]): ICostsClustering[] => {
  const status: TaskStatus[] = [['upcoming', 'Upcoming'], ['completed', 'Completed'], ['in_progress', 'In Progress']]
  const clustered: ICostsClustering[] = status.map(s => {
    return {
      name: s[1],
      costs: _costs.filter(a => a.status === s[0])
    }
  })
  return clustered
}

const phaseClustering = (_costs: IDirectCost[]): ICostsClustering[] => {
  const phases = _costs.map(a => a.project_phase)
    .filter((_phase, idx, _phases) => idx === _phases.findIndex(_p => _p?.id === _phase?.id))
    .toSorted((a, b) => {
      const aStartDate = new Date(a.start_date).getTime()
      const bStartDate = new Date(b.start_date).getTime()
      const aEndDate = new Date(a.end_date).getTime()
      const bEndDate = new Date(b.end_date).getTime()
      return aStartDate !== bStartDate ?
        aStartDate - bStartDate
        : aEndDate - bEndDate
    }
    )
  const clustered: ICostsClustering[] = phases.map(phase => {
    return {
      name: phase?.name,
      phase: phase,
      costs: _costs.filter(a => a.project_phase && phase && +a.project_phase.id === +phase.id)
    }
  }).filter(_d => _d.costs.length !== 0)

  return clustered
}

const phaseOfferClustering = (_costs: IOfferDirectCost[]): ICostsClustering[] => {
  const phases = _costs.map(a => a.project_phase)
    .filter((_phase, idx, _phases) => idx === _phases.findIndex(_p => _p?.phase === _phase?.phase))
    .toSorted((a, b) => {
      const aStartDate = new Date(a.start_date).getTime()
      const bStartDate = new Date(b.start_date).getTime()
      const aEndDate = new Date(a.end_date).getTime()
      const bEndDate = new Date(b.end_date).getTime()
      return aStartDate !== bStartDate ?
        aStartDate - bStartDate
        : aEndDate - bEndDate
    }
    )
  const clustered: ICostsClustering[] = phases.map(phase => {
    return {
      name: phase?.name,
      phase: phase,
      costs: _costs.filter(c => c.project_phase && phase && +c.project_phase.phase === +phase.phase)
    }
  }).filter(_d => _d.costs.length !== 0)

  return clustered
}

const departmentClustering = (_costs: IDirectCost[]): ICostsClustering[] => {
  const deps = _costs.map(a => a.department).filter((_dep, idx, _deps) => idx === _deps.findIndex(_d => _d?.id === _dep?.id) && _dep)
  if (deps.length !== 0) {
    const clustered: ICostsClustering[] = deps.toSorted((a, b) => a.name.localeCompare(b.name)).map(dep => {
      return {
        name: dep?.name,
        costs: _costs.filter(a => a.department && dep && +a.department.id === +dep.id)
      }
    }).filter(_d => _d.costs.length !== 0)

    return clustered
  }
  return []
}

const departmentOfferClustering = (_costs: IOfferDirectCost[]): ICostsClustering[] => {
  const deps = _costs.map(a => a.department).filter((_dep, idx, _deps) => idx === _deps.findIndex(_d => _d?.id === _dep?.id) && _dep)
  if (deps.length !== 0) {
    const clustered: ICostsClustering[] = deps.map(dep => {
      return {
        name: dep?.name,
        costs: _costs.filter(a => a.department && dep && +a.department.id === +dep.id)
      }
    }).filter(_d => _d.costs.length !== 0)

    return clustered
  }
  return []
}

export const getDirectCostSearchSelector = createSelector(
  [getDirectCostsSelector, getSearchValue],
  (direct_costs, searchValue) => {
    if (searchValue.search !== '') {
      return direct_costs.filter(
        dc => dc.offer_position.toLowerCase().includes(searchValue.search.toLowerCase())
      )
    }
    return direct_costs
  }
)

export const getFilteredDirectCosts = createSelector(
  getFilterCostsCount,
  getDirectCostSearchSelector,
  getFilterCountSelector,
  (costFilters, costs, filterCount) => {
    const departmentFilters = flattenFilters('Departments', costFilters)
    const memberFilters = flattenFilters('Members', costFilters)
    const statusFilters = flattenFilters('Status', costFilters)
    const dueDateFilters = flattenFilters('Due Date', costFilters)
    if (filterCount > 0) {
      const _byDepartment: IDirectCost[] = costs.filter(cost => {
        if (departmentFilters.includes(cost.department.id.toString())) {
          return cost
        }
      })
      const _byMember: IDirectCost[] = (departmentFilters.length > 0 ? _byDepartment : costs).filter(cost => {
        if (memberFilters.some((item) => cost.booked_employees?.map(b => +b.id).includes(+item))) {
          return cost
        }
      })
      const _byStatus: IDirectCost[] = (departmentFilters.length > 0 ? _byDepartment : memberFilters.length > 0 ? _byMember : costs).filter(cost => {
        if (statusFilters.includes(cost.status)) {
          return cost
        }
      })
      const _byDueDate: IDirectCost[] = (statusFilters.length > 0 ? _byStatus : memberFilters.length > 0 ? _byMember : departmentFilters.length > 0 ? _byDepartment : costs).filter(cost => {
        if (dueDateFilters.length > 0) {
          if (cost.end_date) {
            const _obj_end_date = moment(new Date(cost.end_date)).format('YYYY-MM-DD')
            const _todayDate = moment(new Date()).format('YYYY-MM-DD')
            if (dueDateFilters.includes('individual')) {
              const activeIndividual = costFilters
                .filter(f => f.name === 'Due Date')
                .map(f => f.options.map((opt) => opt))
                .flat()
                .filter(o => o.selected)
                .map(o => o)
              const _start_date = activeIndividual[0] && activeIndividual[0].start_date !== undefined ? moment(new Date(activeIndividual[0].start_date)).format('YYYY-MM-DD') : null
              const _end_date = activeIndividual[0] && activeIndividual[0].end_date !== undefined ? moment(new Date(activeIndividual[0].end_date)).format('YYYY-MM-DD') : null
              if (_start_date !== null && _end_date !== null) {
                if (moment(_obj_end_date).isSameOrBefore(_end_date) && moment(_obj_end_date).isSameOrAfter(_start_date)) {
                  return cost
                }
              }
            }
            if (dueDateFilters.includes('today') && moment(_obj_end_date).isSame(_todayDate)) {
              return cost
            }
            if (dueDateFilters.includes('this_week') && moment(_todayDate).isSame(_obj_end_date, 'year')
              && moment(_todayDate).isSame(_obj_end_date, 'week')) {
              return cost
            }
            if (dueDateFilters.includes('this_month') && moment(_todayDate).isSame(_obj_end_date, 'month')) {
              return cost
            }
            if (dueDateFilters.includes('overdue') && (cost.status === 'in_progress' || cost.status === 'upcoming') && moment(_obj_end_date).isBefore(_todayDate)) {
              return cost
            }
          }
        } else {
          return cost
        }
      })
      return [..._byDueDate]
    }
    return costs
  }
)


export const sortDirectCosts = (costs: IDirectCost[], sortClustering: ICostSorting): IDirectCost[] => {
  if (sortClustering) {
    let sortedCosts = costs
    if (sortClustering.column.name === 'department') {
      sortedCosts.sort((a, b) => a.department.name.localeCompare(b.department.name))
    } else if (sortClustering.column.name === 'offer_position') {
      sortedCosts = sortTasksByKey(sortedCosts, 'key', 'offer_position')
    } else if (sortClustering.column.name === 'timelines') {
      sortedCosts.sort((a, b) => moment(a.end_date).diff(moment(b.end_date)))
    } else if (sortClustering.column.name === 'phase') {
      sortedCosts.sort((a, b) => a.project_phase.name.localeCompare(b.project_phase.name))
    } else if (sortClustering.column.name === 'status') {
      sortedCosts.sort((a, b) => a.status.localeCompare(b.status))
    } else if (sortClustering.column.name === 'time progress') {
      sortedCosts.sort((a, b) => +a.time_completion - +b.time_completion)
    } else if (sortClustering.column.name === 'tracked hours') {
      sortedCosts.sort((a, b) => +a.current_hours !== +b.current_hours ? +a.current_hours - +b.current_hours : +a.total_hours - +b.total_hours)
    } else if (sortClustering.column.name === 'remaining') {
      sortedCosts.sort((a, b) => +a.remainingHours - +b.remainingHours)
    } else if (sortClustering.column.name === 'quantity') {
      sortedCosts.sort((a, b) => +a.quantity - +b.quantity)
    } else if (sortClustering.column.name === 'unit price') {
      sortedCosts.sort((a, b) => +a.unit_price - +b.unit_price)
    } else if (sortClustering.column.name === 'total costs') {
      sortedCosts.sort((a, b) => (+a.unit_price * +a.quantity) - (+b.unit_price * +b.quantity))
    } else if (sortClustering.column.name === 'unit hours') {
      sortedCosts.sort((a, b) => +a.unit_hours - +b.unit_hours)
    } else if (sortClustering.column.name === 'total hours') {
      sortedCosts.sort((a, b) => a.total_hours && b.total_hours ? +a.total_hours - +b.total_hours : 1)
    }

    if (sortClustering.column.direction === 'desc') {
      sortedCosts.reverse()
    }
    return sortedCosts
  }
  return costs
}

export const sortOfferDirectCosts = (costs: IOfferDirectCost[], sortClustering: ICostSorting): IOfferDirectCost[] => {
  if (sortClustering) {
    let sortedCosts = costs
    if (sortClustering.column.name === 'department') {
      sortedCosts.sort((a, b) => a.department.name.localeCompare(b.department.name))
    } else if (sortClustering.column.name === 'offer_position') {
      sortedCosts = sortTasksByKey(sortedCosts, 'key', 'offer_position')
    } else if (sortClustering.column.name === 'timelines') {
      sortedCosts.sort((a, b) => moment(a.end_date).diff(moment(b.end_date)))
    } else if (sortClustering.column.name === 'phase') {
      sortedCosts.sort((a, b) => a.project_phase.name.localeCompare(b.project_phase.name))
    } else if (sortClustering.column.name === 'quantity') {
      sortedCosts.sort((a, b) => +a.quantity - +b.quantity)
    } else if (sortClustering.column.name === 'unit price') {
      sortedCosts.sort((a, b) => +a.unit_price - +b.unit_price)
    } else if (sortClustering.column.name === 'total costs') {
      sortedCosts.sort((a, b) => (+a.unit_price * +a.quantity) - (+b.unit_price * +b.quantity))
    } else if (sortClustering.column.name === 'unit hours') {
      sortedCosts.sort((a, b) => +a.unit_hours - +b.unit_hours)
    } else if (sortClustering.column.name === 'total hours') {
      sortedCosts.sort((a, b) => a.total_hours && b.total_hours ? +a.total_hours - +b.total_hours : 1)
    }

    if (sortClustering.column.direction === 'desc') {
      sortedCosts.reverse()
    }
    return sortedCosts
  }
  return costs
}

export const getClusteredDirectCostsSelector = createSelector(
  [getSortClustering, getFilteredDirectCosts],
  (sortClustering: ICostSorting, costs: IDirectCost[]) => {
    if (costs) {
      const sortedCosts = sortDirectCosts(costs, sortClustering)

      if (sortClustering.clustering === 'completion') {
        return statusClustering(sortedCosts)
      }
      if (sortClustering.clustering === 'overall') {
        return [{ name: 'Overall', costs: sortedCosts }]
      }
      if (sortClustering.clustering === 'phase') {
        return phaseClustering(sortedCosts)
      }
      if (sortClustering.clustering === 'department') {
        return departmentClustering(sortedCosts)
      }
    }
  }
)

export const getClusteredOfferDirectCostsSelector = createSelector(
  [getSortClustering, getOfferDirectCostState],
  (
    sortClustering: ICostSorting = {
      clustering: "phase",
      column: { name: "offer_position", direction: "asc" }
    },
    costs: IOfferDirectCost[]
  ) => {
    if (costs) {
      const sortedCosts = sortOfferDirectCosts(costs, sortClustering)

      if (sortClustering.clustering === 'overall') {
        return [{ name: 'Overall', costs: sortedCosts }]
      }
      if (sortClustering.clustering === 'phase') {
        return phaseOfferClustering(sortedCosts)
      }
      if (sortClustering.clustering === 'department') {
        return departmentOfferClustering(sortedCosts)
      }
    }
  }
)

const calcTotal = (costs: number[]) => {
  const calc = costs.reduce((a, b) => (a ? a : 0) + (b ? b : 0), 0)
  return calc ? calc : 0
}

export const getOfferTotalWidgetSelector = createSelector(
  getDirectCostsSelector,
  getPassTroughCostsSelector,
  (direct_costs, pass_trough_costs) => {
    const direct_costs_total = calcTotal(direct_costs.map(dc => +dc.unit_price * +dc.quantity))
    const pass_trough_costs_total = calcTotal(pass_trough_costs.map(dc => +dc.unit_price * +dc.quantity))
    const offer_total = direct_costs_total + pass_trough_costs_total
    let _forecast = 0
    let _budget = 0
    direct_costs.forEach(dc => {
      _forecast += dc.forecast
      _budget += dc.budget
    })
    const projected_profit = +calcProfit(_budget, _forecast)
    const total: ICostWidgetTotal = {
      direct_cost_total: direct_costs_total,
      pass_trough_total: pass_trough_costs_total,
      offer_total: offer_total,
      projected_profit: projected_profit
    }
    return total
  }
)

export const sortMilestoneTasks = (milestones: IMilestone[], sortClustering: IMilestoneSortColumn): IMilestone[] => {
  if (sortClustering) {
    return milestones.map(milestone => {
      const copiedMilestone = {...milestone}
      // A copy, and the result is assigned back below.
      //
      // `{...milestone}` is shallow, so `copiedMilestone.tasks` is the store's own array. The
      // branches below sort it IN PLACE, which is how ordering used to take effect at all — and
      // it is why swapping in sortTasksByKey silently broke this: that returns a NEW array, so
      // `sortedCosts` pointed at the sorted copy while the milestone being returned still
      // carried the unsorted original. Clicking the Task column did nothing; every other column
      // kept working, because `.sort()` mutates.
      let sortedCosts = [...copiedMilestone.tasks]
      if (sortClustering.name === 'offer_position') {
        sortedCosts = sortTasksByKey(sortedCosts, 'offer_direct_cost.key', 'offer_direct_cost.offer_position')
      }
      if (sortClustering.name === 'department') {
        sortedCosts.sort((a, b) => a.offer_direct_cost.department.name.localeCompare(b.offer_direct_cost.department.name))
      }
      if (sortClustering.name === 'offered') {
        sortedCosts.sort((a, b) => a.offered - b.offered)
      }
      if (sortClustering.name === 'planned') {
        sortedCosts.sort((a, b) => a.planned - b.planned)
      }
      if (sortClustering.name === 'performed') {
        sortedCosts.sort((a, b) => a.performed - b.performed)
      }
      if (sortClustering.direction === 'desc') {
        sortedCosts.reverse()
      }
      copiedMilestone.tasks = sortedCosts
      return copiedMilestone
    })
  }
  return milestones
}

const sortMilestones = (milestones: IMilestone[]): IMilestone[] => {
  const unassignedMilestone = milestones.find(m => m.date === null)
  const sortedMilestones = milestones.filter(m => m.date !== null).toSorted((a, b) => {
    const aStartDate = new Date(a.date).getTime()
    const bStartDate = new Date(b.date).getTime()
    const aEndDate = new Date(a.date).getTime()
    const bEndDate = new Date(b.date).getTime()
    return aStartDate !== bStartDate ?
      aStartDate - bStartDate
      : aEndDate - bEndDate
  })
  if (unassignedMilestone) {
    return [...sortedMilestones, unassignedMilestone]
  }
  return sortedMilestones
}

export const getMilestonesWithTasksSelector = createSelector(
  [getSortTasks, getMilestones],
  (sort: IMilestoneSortColumn, milestones: IMilestone[]) => {
    const sortedMilestones = sortMilestones(milestones)
    return sortMilestoneTasks(sortedMilestones, sort)
  }
)

const sortTrackerMilestones = (unitTracker: IMilestoneWithUnitTracker[]): IMilestoneWithUnitTracker[] => {
  const unassigned = unitTracker.find(m => m.date === null)
  const sorted = unitTracker.filter(m => m.date !== null).toSorted((a, b) => {
    const aStartDate = new Date(a.date).getTime()
    const bStartDate = new Date(b.date).getTime()
    const aEndDate = new Date(a.date).getTime()
    const bEndDate = new Date(b.date).getTime()
    return aStartDate !== bStartDate ?
      aStartDate - bStartDate
      : aEndDate - bEndDate
  })
  if (unassigned) {
    return [...sorted, unassigned]
  }
  return sorted
}

const sortMilestonesTrackerTasks = (milestones: IMilestoneWithUnitTracker[], sortClustering: IMilestoneSortColumn): IMilestoneWithUnitTracker[] => {
  return milestones.map(milestone => {
    const copiedMilestone = {...milestone}
    let sortedCosts = copiedMilestone.tasks
    if (sortClustering.name === 'offer_position') {
      sortedCosts = sortTasksByKey(sortedCosts, 'offer_direct_cost.key', 'offer_direct_cost.offer_position')
    }
    return { ...copiedMilestone, tasks: sortedCosts }
  })
}

export const getMilestoneUnitTrackerSelector = createSelector(
  [getMilestoneUnitTracker, getMilestones],
  (milestoneUnitTracker: IMilestoneOfferDirectCostMonths[], milestones: IMilestone[]) => {
    const _milestones: IMilestoneWithUnitTracker[] = milestones.map(m => ({
      ...m,
      tasks: milestoneUnitTracker.filter(t => t.milestone.id === m.id)
    }))
    const sortedMilestones = sortTrackerMilestones(_milestones)
    return sortMilestonesTrackerTasks(sortedMilestones, { name: "offer_position", direction: "asc" })
  }
)
